import { differenceWith } from "ramda";
import { firestore, videosStorageRef } from "../../../init/firebase";
import { CollectionReference, DocReference } from "../../../types/firebase.types";
import { applyCreateTimestamp, applyUpdateTimestamp } from "../../../utils/timestamp";

export interface MaterialDocument {
  input: string;
  src: string;
  type: string;
  name: string;
  inputPath: string;
  outputPath: string;
  ref: DocReference | null;
}

export const getAddedAndRemovedVideos = (
  originalVideos: (File | { src: string })[],
  modifiedVideos: (File | { src: string })[]
) => {
  const comp = (a: any, b: any) => a.name === b.name;

  const addedVideos: any[] = differenceWith(comp, modifiedVideos, originalVideos);
  const removedVideos: any[] = differenceWith(comp, originalVideos, modifiedVideos);

  return {
    addedVideos,
    removedVideos,
  };
};

export const getVideosDifference = (
  firstArray: (File | MaterialDocument)[],
  secondArray: (File | MaterialDocument)[]
) => {
  const comp = (a: any, b: any) => a.name === b.name;
  return {
    difference: differenceWith(comp, firstArray, secondArray),
  };
};

/**
 * this function is used to batch write a data to a specific collection
 * */
export const baseFirestoreUpload = async (
  length: number,
  collection: CollectionReference,
  data: MaterialDocument = {
    input: "",
    src: "",
    type: "video",
    name: "",
    inputPath: "",
    outputPath: "",
    ref: null,
  }
) => {
  const uploadedRefs: DocReference[] = [];
  const batch = firestore.batch();
  for (const _ of Array(length).fill(1)) {
    const videoRef = collection.doc();
    const { ref, ...dataToSend } = data;
    batch.set(videoRef, applyCreateTimestamp(dataToSend));
    uploadedRefs.push(videoRef);
  }
  await batch.commit();

  return { uploadedRefs };
};

export const uploadVideosToStorage = async (videos: (File | MaterialDocument)[]) => {
  const uploaded: Omit<MaterialDocument, "ref">[] = [];

  for (const video of videos) {
    if (video instanceof File) {
      // upload video to firestore bucket and get back the src
      // push the required uploaded video data to the uploaded array
      const videoRef = videosStorageRef.child(`input/${video.name}`);
      const uploadedTask = await videoRef.put(video);
      const downloadUrl = await videoRef.getDownloadURL();

      uploaded.push({
        input: downloadUrl,
        src: downloadUrl,
        type: "video",
        name: uploadedTask.metadata.name,
        inputPath: uploadedTask.metadata.fullPath,
        outputPath: uploadedTask.metadata.fullPath,
      });
    } else {
      // video already uploaded, push it direct to the uploaded array
      uploaded.push(video);
    }
  }

  return uploaded;
};

export const handleUploadMaterials = async (
  videos: (File | MaterialDocument)[],
  collection: CollectionReference,
  data: MaterialDocument = {
    input: "",
    src: "",
    type: "video",
    name: "",
    inputPath: "",
    outputPath: "",
    ref: null,
  }
) => {
  const storageUploadingProgress: Promise<any>[] = [];
  const batch = firestore.batch();

  for (const video of videos) {
    if (video instanceof File) {
      // upload video to firestore bucket and get back the src
      // push the required uploaded video data to the uploaded array
      const storageMaterialRef = videosStorageRef.child(`input/${video.name}`);
      const firestoreDocumentRef = collection.doc();
      const { ref, ...dataToSend } = data;
      batch.set(firestoreDocumentRef, applyCreateTimestamp(dataToSend));

      storageUploadingProgress.push(
        storageMaterialRef.put(video).then((uploadedTask) => {
          storageMaterialRef.getDownloadURL().then((downloadUrl) => {
            firestoreDocumentRef.update(
              applyUpdateTimestamp({
                input: downloadUrl,
                type: "video",
                name: uploadedTask.metadata.name,
                inputPath: uploadedTask.metadata.fullPath,
              })
            );
          });
        })
      );
    }
  }

  await batch.commit();
  await Promise.all(storageUploadingProgress);
};

export const deleteVideosFromStorage = async (videos: MaterialDocument[]) => {
  for (const video of videos) {
    const ref = videosStorageRef.child(video.inputPath);
    try {
      await ref.delete();
    } catch (ex: any) {
      console.log(ex.message);
    }
  }
};

export const deleteMaterialDocuments = async (materials: MaterialDocument[]) => {
  const batch = firestore.batch();
  for (const material of materials) {
    try {
      if (material.ref) batch.delete(material.ref!);
    } catch (ex: any) {
      console.log(ex.message);
    }
  }
  await batch.commit();
};
